import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";
import { Button, Modal, CardContent, TextField, Chip, Box, Typography, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import MainCard from 'components/MainCard';
import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals';
import axios from 'axios'; // 또는 프로젝트에서 사용하는 HTTP 클라이언트
import { getCoordsByAddressZh } from 'components/axios/geocode';

export function YihaiAddressSearchLayer({ register, field, fieldState, setAddressInfo, objName, defaultVal = '' }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);

    const [keyword, setKeyword] = useState("");
    const [results, setResults] = useState([]);
    const [selectedItem, setSelectedItem] = useState(null); // 백엔드에서 받은 선택 객체 ({ code, name, fullName })
    const [selectAddress, setSelectAddress] = useState(defaultVal || "");
    const [remainAddress, setRemainAddress] = useState('');
    const [loading, setLoading] = useState(false);

    const { openModal } = useModals();
    const handleAlertClick = (alertMessage) => {
        openModal(modals.alertModal, {
            onClose: () => {},
            message: alertMessage,
        });
    };

    const handleOpen = () => {
        setOpen(true);
        if (!defaultVal) setSelectAddress('');
    };

    const handleClose = () => {
        setOpen(false);
    };

    useEffect(() => {
        setSelectAddress(defaultVal);
    }, [defaultVal]);

    // ==========================================
    // 백엔드 API 키워드 검색
    // ==========================================
    const handleSearch = async () => {
        if (!keyword.trim()) {
            handleAlertClick(t('message.common.error.text.address.input'));
            return;
        }

        setLoading(true);
        try {
            // TODO: 실제 백엔드 API Endpoint에 맞게 수정하세요.
            const response = await getCoordsByAddressZh(keyword.trim());
            const data = response || [];
            
            setResults(data);
            setSelectedItem(null); // 검색 시 이전 선택 항목 초기화
        } catch (err) {
            console.error("An error occurred while searching the address API.:", err);
            handleAlertClick(t('message.common.error.text.search.failed') || "An error occurred during the search.");
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    // 검색 결과 항목 선택
    const handleSelect = (candidate) => {
        setSelectedItem(candidate);
        setRemainAddress(''); // 선택 시 상세주소 초기화
    };

    // 선택 완료 및 부모창 전달
    const handleWrite = () => {
        if (!selectedItem) return;

        // 최종 표시용 주소 문자열 (전체 행정구역명 + 상세주소)
        const fullAddressStr = (selectedItem.fullName + remainAddress).trim();
        // const fullAddressStr = (selectedItem.fullName + ' ' + remainAddress).trim();

        // 1. 부모 컴포넌트에 Echarts 연동용 code와 주소 정보 전달
        // const resultPayload = {
        //     code: selectedItem.code,           // Echarts 지도 렌더링용 행정구역 코드
        //     name: selectedItem.name,           // 구역명 (예: 崇川区)
        //     fullName: selectedItem.fullName,   // 전체 행정구역 경로명 (예: 江苏省南通市崇川区)
        //     address_name: fullAddressStr,     // 화면 표시용 전체 주소
        //     remainAddress: remainAddress.trim()// 상세주소
        // };

        // setAddressInfo?.(resultPayload);
        setAddressInfo?.({address_name: fullAddressStr, chinaRegionCode:selectedItem.code})
        setSelectAddress(fullAddressStr);

        // 2. React Hook Form 또는 부모 field onChange 연동
        field?.onChange?.(fullAddressStr);

        // 3. 모달 내부 상태 초기화 및 닫기
        setKeyword('');
        setSelectedItem(null);
        setResults([]);
        setRemainAddress('');
        handleClose();
    };

    return (
        <React.Fragment>
            <TextField
                id={objName}
                {...(field || {})}
                {...(register ? register(objName, { required: t('message.common.error.text.address.input') }) : {})}
                value={selectAddress}
                InputProps={{ readOnly: true }}
                sx={{ m: 1, minWidth: '70%', width: '70%', cursor: 'pointer' }}
                onClick={handleOpen}
            />

            <Modal className="pop_box" open={open} onClose={handleClose} aria-labelledby="address-search-modal">
                <MainCard sx={{ maxWidth: 600, width: "90%", maxHeight: "80vh", margin: "auto", mt: "10vh" }} modal title="" content={false}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <img src={`${process.env.PUBLIC_URL}/img/icon/popbullet.png`} alt="" style={{ height: 16 }} />
                            <Chip label={t('message.common.title.address.search')} color="success" size="small" />
                        </Box>
                        <Button variant="contained" color="primary" size="small" onClick={handleClose}>
                            {t('message.common.btn.close')}
                        </Button>
                    </Box>

                    <CardContent sx={{ p: 2 }}>
                        {/* 검색어 입력 영역 */}
                        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                            <TextField
                                fullWidth
                                size="small"
                                id={`${objName}_search_input`}
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                                placeholder={t('message.common.error.text.address.input')}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
                            />
                            <Button 
                                variant="contained" 
                                color="info" 
                                size="medium" 
                                onClick={handleSearch} 
                                disabled={loading}
                                sx={{ minWidth: 80 }}
                            >
                                {loading ? "..." : t('message.common.btn.search')}
                            </Button>
                        </Box>

                        {/* 검색 결과 목록 영역 */}
                        {results.length > 0 && (
                            <Box sx={{ border: "1px solid #ccc", borderRadius: 1, p: 1 }} className="addressSearchList">
                                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
                                    {t('message.common.alert.use.address.select')}
                                </Typography>
                                <List sx={{ maxHeight: 180, overflowY: "auto", p: 0 }}>
                                    {results.map((item) => {
                                        const isSelected = selectedItem?.code === item.code;
                                        return (
                                            <ListItem disablePadding key={item.code}>
                                                <ListItemButton
                                                    selected={isSelected}
                                                    onClick={() => handleSelect(item)}
                                                    sx={{ py: 0.8, px: 1, borderBottom: "1px dashed #e0e0e0" }}
                                                >
                                                    <ListItemText 
                                                        primary={item.fullName} 
                                                        // secondary={`코드: ${item.code}`}
                                                        primaryTypographyProps={{ fontSize: '0.875rem' }} 
                                                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                                                    />
                                                </ListItemButton>
                                            </ListItem>
                                        );
                                    })}
                                </List>

                                {/* 상세 주소 입력 영역 */}
                                {selectedItem && (
                                    <Box sx={{ display: "flex", gap: 1, mt: 2, pt: 1, borderTop: "1px solid #eee" }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            id="remainAddress"
                                            value={remainAddress}
                                            onChange={(e) => setRemainAddress(e.target.value)}
                                            placeholder={t('message.common.text.enter.remain.address')}
                                            onKeyDown={(e) => { if (e.key === 'Enter') handleWrite(); }}
                                        />
                                        <Button variant="contained" color="success" size="small" onClick={handleWrite} sx={{ minWidth: 80 }}>
                                            {t('message.common.btn.write')}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </CardContent>
                </MainCard>
            </Modal>
        </React.Fragment>
    );
}

export default YihaiAddressSearchLayer;